import type { DownloadDeduplicator } from '@mimir/client-common/download/downloadDeduplicator';
import type { DownloadAndMuxTask, ProgressHandler } from '@mimir/client-common/download/downloadAndMuxTask';
import type { CancelToken, CancelTokenSource } from 'axios';
import type { DownloadProgressDto } from '@mimir/common/dto/transferRequestDtos';
import Axios from 'axios';
import { createUuid } from '@mimir/common/util/createUuid';

interface Task {
  cts: CancelTokenSource,
  ongoingPromise: Promise<void> | null;
  progressHandlers: Record<string, ProgressHandler>;
}

class DownloadDeduplicatorImpl implements DownloadDeduplicator {
  private tasks: Record<string, Task> = { };

  deduplicate(taskId: string, downloadAndMuxTask: DownloadAndMuxTask): DownloadAndMuxTask {
    if (!(taskId in this.tasks)) {
      this.tasks[taskId] = {
        cts: Axios.CancelToken.source(),
        ongoingPromise: null,
        progressHandlers: { },
      };
    }

    const taskInstanceId = createUuid();

    const task = this.tasks[taskId];

    return (onProgress: ProgressHandler, ct: CancelToken): Promise<void> => {
      task.progressHandlers[taskInstanceId] = onProgress;
      if (task.ongoingPromise === null) {
        const aggregatedProgressHandler = async (downloadStats: DownloadProgressDto) => {
          for (const progressHandler of Object.values(task.progressHandlers)) {
            await progressHandler(downloadStats);
          }
        };

        task.ongoingPromise =
          downloadAndMuxTask(aggregatedProgressHandler, task.cts.token)
            .finally(() => {
              delete this.tasks[taskId];
            });
      }

      const ongoingPromise = task.ongoingPromise;

      return new Promise<void>((resolve, reject) => {
        ct.promise
          .then((cancel) => {
            delete task.progressHandlers[taskInstanceId];

            if (Object.values(task.progressHandlers).length === 0) {
              console.log(`Cancelling download taskId ${taskId} since all the task instances were cancelled`);
              task.cts.cancel();
            }
            reject(cancel);
          })
          .catch((error) => {
            // As far as I know this will not be called because the cancel token
            // promise can't fail.
            reject(error);
          });

        ongoingPromise
          .then(() => resolve())
          .catch((error) => reject(error));
      });
    };
  }
}

export const globalDownloadDeduplicator : DownloadDeduplicator = new DownloadDeduplicatorImpl();

